import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore, setSessionStore } from "app/utils/sessionStore";
import { ProgressSpinner } from "primereact/progressspinner";

import PlanOverviewSection from "app/pages/InteractiveReviewCenter/components/PlanOverviewSection";
import FinancingCard from "app/pages/InteractiveReviewCenter/components/FinancingCard";
import PolicyInformationCard from "../components/PolicyInformationCard";
import PolicyOverviewCard from "../components/PolicyOverviewCard";
import OutsidePledgedAssetsCard from "../components/OutsidePledgedAssetsCard";
import YearStepper, { YearStep } from "app/pages/InteractiveProposal/YearStepper";
import WarningHex from "app/assets/IconComponent/WarningHex";
import { ProposalTypes, planHasCreditType } from "app/utils/types";
import { ThemeContext } from "app/uicomponents/macrocomponents/RootLayout";

// ─── API response types ────────────────────────────────────────────────────────

interface PlanReview {
  year: number;
  planYear: number;
  reviewCompleted: string | null;
  confirmedBy: number;
  skippedBy: number;
  total: number;
  reviewOpenDate?: string | null;
  anniversaryDate?: string | null;
}

// A single life/disability policy carried on a shareholder/employee.
interface PersonPolicy {
  id: string;
  policyNumber: string;
  policyType?: string | null;
  policySubType?: string | null;
  benefit?: number | null;
  insuranceCompany?: {
    name?: string;
    logoPath?: string | null;
    insuranceCompanyBrandings?: {
      primaryLogo?: string | null;
      primaryLogoDark?: string | null;
      primaryColor?: string;
      primaryColorDark?: string;
    } | null;
  } | null;
  ownershipEntity?: { title?: string; logoUrl?: string | null } | null;
  beneficiaryName?: string | null;
  beneficiaryProfilePicture?: string | null;
}

// Buy Sell shareholder / Key Person employee — shared shape with embedded policies.
interface Person {
  id: string;
  name?: string;
  userId?: string;
  ownershipPercentage?: string | null;
  // BS supplies shareholderValue; KP supplies valuation.
  shareholderValue?: number | null;
  valuation?: number | null;
  user?: {
    profile?: { firstName?: string; lastName?: string; profilePicture?: string | null };
  };
  lifePolicies?: PersonPolicy[];
  disabilityPolicies?: PersonPolicy[];
}

interface ReviewCenterSummaryData {
  planType: string;
  planSubType: string;
  planEffectiveDate: string;
  proposalNo: string;
  lastValuationDate?: string | null;
  businessValuation?: number | null;
  // Key Person payload uses these field names instead of lastValuationDate/businessValuation.
  valuation?: number | null;
  valuationDate?: string | null;
  entity: { title: string; logoUrl: string };
  contacts:
    | Array<{ id?: string; profile: { firstName: string; lastName: string } }>
    | { profile: { firstName: string; lastName: string } };
  // Buy Sell people (the "Shareholders" card source) — carry their own policies.
  shareholders?: Person[];
  // Key Person people (the "Key Persons" card source) — carry their own policies.
  employees?: Person[];
  phases: {
    funding: { completed: number; total: number; progress: number };
    growth: { completed: number; total: number; progress: number };
  };
  selectedYear: number;
  selectedCreditType: string;
  planReviews: PlanReview[];
  financing: {
    lender: { logoUrl: string; name: string };
    financingNum: string;
    borrower: { title: string; logoUrl: string };
    interestTreatment: string;
    expectedPayoff: string;
    initialRate: number;
    financingValue: number;
    rateTerms: string;
  };
  policies: Array<{
    insured: { user: { profile: { firstName: string; lastName: string; profilePicture: string } } };
    policyNumber: string;
    policyType?: string;
    policySubType?: string;
    PolicyReview: Array<{
      policyAccountValue: number;
      policySurrenderValue: number;
      policyDB: number;
    }>;
    insuranceCompany: {
      logoPath: string;
      name: string;
      insuranceCompanyBrandings: {
        primaryColor: string;
        primaryColorDark: string;
        primaryLogo?: string;
        primaryLogoDark?: string;
      };
    };
    beneficiary: string;
  }>;
  outsideAssetsLedger: Array<{
    id: string;
    type: string;
    institution: string;
    accountNumber: string;
    value: number;
    creditPercentage: number;
    creditValue: number;
  }>;
  policyOverview?: {
    shareholders: number;
    lifePolicies: number;
    disabilityPolicies: number;
    shareholderValue: number;
    lifeBenefits: number;
    disabilityBenefits: number;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (value: number): string => {
  if (value == null) return "—";
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${value.toLocaleString()}`;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const titleCase = (str: string) =>
  str
    ?.replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatCreditTypeLabel = (ct: string) => (ct === "postCredit" ? "Post-Credit" : "Pre-Credit");

// ─── Map API response to YearStep[] ───────────────────────────────────────────

function mapReviewsToSteps(
  reviews: PlanReview[],
  selectedYear: number,
  selectedCreditType: string,
  // Pre-Credit/Post-Credit applies to Premium Finance and Split Dollar — Buy Sell
  // and Key Person just show review status.
  showCreditLabel = false,
): YearStep[] {
  return (reviews ?? []).map((r) => {
    // No review window configured → locked
    if (!r.reviewOpenDate) {
      return { yearNumber: r.planYear, calendarYear: r.year, status: "LOCKED" };
    }

    const storedDate = getSessionStore("currentDate");
    const now = storedDate === true ? new Date(r.reviewOpenDate).getTime() + 1 : Date.now();

    // Outside review window (before open or after anniversary) → locked
    if (
      now < new Date(r.reviewOpenDate).getTime() ||
      (r.anniversaryDate && now > new Date(r.anniversaryDate).getTime())
    ) {
      return { yearNumber: r.planYear, calendarYear: r.year, status: "LOCKED" };
    }

    // Window is open — derive status from review counts (null-safe)
    const confirmed = r.confirmedBy ?? 0;
    const skipped = r.skippedBy ?? 0;
    const total = r.total ?? 0;

    if (total > 0 && confirmed === total) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "COMPLETED",
        completed: true,
        label: "Confirm (All)",
      };
    }
    if (total > 0 && skipped === total) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "SKIPPED",
        label: "Skip (All)",
      };
    }
    if (confirmed > 0) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "COMPLETED",
        label: `Confirm (${confirmed})`,
      };
    }
    if (skipped > 0) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "SKIPPED",
        label: `Skip (${skipped})`,
      };
    }

    // Open window, nothing reviewed yet → CURRENT (clock); credit label (Premium
    // Finance only) shows on the selected year.
    return {
      yearNumber: r.planYear,
      calendarYear: r.year,
      status: "CURRENT",
      label:
        showCreditLabel && r.planYear === selectedYear
          ? formatCreditTypeLabel(selectedCreditType)
          : undefined,
    };
  });
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const ReviewCenterOverview = () => {
  const planId = getSessionStore("planInfo")?.planId;
  const sessionPlanType = getSessionStore("planInfo")?.planType;
  const { pathname } = useLocation();
  const isReviewSummary = pathname.includes("interactive-review-center/summary");

  const [selectedYear, setSelectedYear] = useState(1);
  const [selectedCreditType, setSelectedCreditType] = useState("preCredit");
  const [summaryData, setSummaryData] = useState<ReviewCenterSummaryData | null>(null);
  const [loading, setLoading] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  const isDarkTheme = isDarkMode;

  // Pre-Credit/Post-Credit phases apply to Premium Finance and Split Dollar,
  // but not to Buy Sell or Key Person.
  const hasCreditPhases = planHasCreditType(sessionPlanType);

  useEffect(() => {
    setSessionStore("ircSelectedYear", selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    setSessionStore("ircSelectedCreditType", selectedCreditType);
  }, [selectedCreditType]);

  useEffect(() => {
    if (!planId) return;
    let cancelled = false;

    setLoading(true);
    // creditType is only relevant to plans with Pre/Post-Credit phases (PF, SD).
    const creditQuery = hasCreditPhases ? `&creditType=${selectedCreditType}` : "";
    makeRequest("interactiveReviewCenterSummary")
      .get(`${planId}?year=${selectedYear}${creditQuery}`, true)
      .then((res: any) => {
        if (cancelled) return;
        const data: ReviewCenterSummaryData = res?.data?.data;
        setSummaryData(data);
      })
      .catch((err: any) => {
        if (!cancelled) console.error("Failed to fetch review center summary:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // eslint-disable-next-line
    return () => {
      cancelled = true;
    };
  }, [planId, selectedYear, selectedCreditType, hasCreditPhases]);

  const yearSteps = useMemo(
    () =>
      summaryData
        ? mapReviewsToSteps(
            summaryData.planReviews,
            selectedYear,
            selectedCreditType,
            hasCreditPhases,
          )
        : [],
    [summaryData, selectedYear, selectedCreditType, hasCreditPhases],
  );

  // ─── Shareholder / Key Person selection ──────────────────────────────────────
  // BS reads from `shareholders`, KP from `employees`; both carry their policies.
  // Selection drives the Exposure Value (Valuation) total, Policy Overview sums,
  // and which rows show in Policy Information.
  const isBuySell = sessionPlanType === ProposalTypes.BuySell;
  const isKeyPerson = sessionPlanType === ProposalTypes.KeyPersonInsurance;
  const isPeoplePlan = isBuySell || isKeyPerson;

  const people = useMemo<Person[]>(
    () => (isBuySell ? summaryData?.shareholders : summaryData?.employees) ?? [],
    [summaryData, isBuySell],
  );

  // Stable key per person, matching PlanOverviewSection's `c.id ?? String(i)`.
  const personKey = (p: Person, i: number) => p.id ?? String(i);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Default everyone selected whenever the people list changes.
  useEffect(() => {
    setSelectedIds(people.map(personKey));
  }, [people]);

  const togglePerson = (id: string) =>
    setSelectedIds((prev) => {
      if (!prev.includes(id)) return [...prev, id];
      // Keep at least one person checked — can't uncheck the last one.
      if (prev.length <= 1) return prev;
      return prev.filter((x) => x !== id);
    });

  const selectedPeople = useMemo(
    () => people.filter((p, i) => selectedIds.includes(personKey(p, i))),
    [people, selectedIds],
  );

  // Aggregate the selected people's value + policies (sum of all when all checked).
  const overview = useMemo(() => {
    const lifePolicies = selectedPeople.flatMap((p) => p.lifePolicies ?? []);
    const disabilityPolicies = selectedPeople.flatMap((p) => p.disabilityPolicies ?? []);
    const sumBenefit = (arr: PersonPolicy[]) =>
      arr.reduce((t, p) => t + (Number(p.benefit) || 0), 0);
    return {
      peopleCount: selectedPeople.length,
      lifeCount: lifePolicies.length,
      disabilityCount: disabilityPolicies.length,
      // BS -> shareholderValue, KP -> valuation.
      value: selectedPeople.reduce((t, p) => t + (p.shareholderValue ?? p.valuation ?? 0), 0),
      lifeBenefits: sumBenefit(lifePolicies),
      disabilityBenefits: sumBenefit(disabilityPolicies),
    };
  }, [selectedPeople]);

  const planPhasesProps = summaryData
    ? {
        planType: summaryData.planType,
        planSubType: summaryData.planSubType,
        effectiveDate: formatDate(summaryData.planEffectiveDate),
        progressPercent: summaryData.phases?.funding?.progress,
        // BS uses lastValuationDate; KP uses valuationDate (shown as "Last Assessment").
        lastValuationDate: formatDate(
          summaryData.lastValuationDate ?? summaryData.valuationDate ?? null,
        ),
        // BS Valuation / KP Exposure Value box = sum across the *selected* people;
        // other plan types fall back to the server-provided business valuation.
        valuation: isPeoplePlan
          ? formatCurrency(overview.value)
          : summaryData.businessValuation ?? summaryData.valuation
          ? formatCurrency((summaryData.businessValuation ?? summaryData.valuation) as number)
          : undefined,
        phases: [
          {
            label: "FUNDING",
            current: summaryData.phases?.funding?.completed ?? 0,
            total: summaryData.phases?.funding?.total ?? 0,
            progress: summaryData.phases?.funding?.progress ?? 0,
            isActive: true,
          },
          {
            label: "GROWTH",
            current: summaryData.phases?.growth?.completed ?? 0,
            total: summaryData.phases?.growth?.total ?? 0,
            progress: summaryData.phases?.growth?.progress ?? 0,
            isActive: false,
          },
        ],
      }
    : undefined;

  const financingRows = summaryData
    ? [
        {
          id: "1",
          lenderName: summaryData?.financing?.lender?.name,
          lenderLogo: summaryData?.financing?.lender?.logoUrl,
          financingNumber: summaryData?.financing?.financingNum,
          borrowerName: summaryData?.financing?.borrower?.title,
          borrowerLogo: summaryData?.financing?.borrower?.logoUrl,
          interestTreatment: titleCase(summaryData?.financing?.interestTreatment),
          initialRate: `${summaryData?.financing?.initialRate}%`,
          financingValue: formatCurrency(summaryData?.financing?.financingValue),
          expectedPayoff: formatDate(summaryData?.financing?.expectedPayoff),
        },
      ]
    : undefined;

  // BS/KP: one Policy Information row per life + disability policy of the
  // *selected* people. Unchecking a person drops all of their policies here.
  const peoplePolicyRows = useMemo(
    () =>
      selectedPeople.flatMap((person) => {
        const insuredName = `${person.user?.profile?.firstName ?? ""} ${
          person.user?.profile?.lastName ?? ""
        }`.trim();
        const insuredAvatar = person.user?.profile?.profilePicture ?? undefined;

        const toRow = (policy: PersonPolicy, kind: string) => {
          const branding = policy.insuranceCompany?.insuranceCompanyBrandings;
          return {
            id: policy.id,
            insuredName,
            insuredAvatar,
            policyNumber: policy.policyNumber,
            // No sub-type / account / surrender in this payload — show the kind + benefit.
            policyType: policy.policyType ?? kind,
            policySubType: policy.policySubType ?? undefined,
            accountValue: "—",
            surrenderValue: "—",
            benefit: formatCurrency(policy.benefit ?? 0),
            ownerName: policy.ownershipEntity?.title ?? summaryData?.entity?.title ?? "—",
            ownerLogo: policy.ownershipEntity?.logoUrl ?? summaryData?.entity?.logoUrl,
            beneficiary: policy.beneficiaryName ?? "—",
            beneficiaryAvatar: policy.beneficiaryProfilePicture ?? undefined,
            policyIcon: branding?.primaryLogo ?? policy.insuranceCompany?.logoPath ?? "",
            primaryColor: branding?.primaryColor,
            primaryColorDark: branding?.primaryColorDark,
            // Insurer logo prefers branding (light/dark), then the raw logoPath.
            insuranceCompanyLogo:
              branding?.primaryLogo ?? policy.insuranceCompany?.logoPath ?? undefined,
            insuranceCompanyLogoDark: branding?.primaryLogoDark ?? undefined,
            insuranceCompanyName: policy.insuranceCompany?.name ?? "",
          };
        };

        return [
          ...(person.lifePolicies ?? []).map((pol) => toRow(pol, "Life")),
          ...(person.disabilityPolicies ?? []).map((pol) => toRow(pol, "Disability")),
        ];
      }),
    [selectedPeople, summaryData],
  );

  const policyRows = isPeoplePlan
    ? peoplePolicyRows
    : summaryData
    ? (summaryData.policies ?? []).map((p, i) => {
        const review =
          p.PolicyReview?.find((r: any) => r.year === selectedYear) ?? p.PolicyReview?.[0];
        const branding = p?.insuranceCompany.insuranceCompanyBrandings;

        return {
          id: p.policyNumber || String(i),
          insuredName: `${p?.insured?.user?.profile?.firstName} ${p?.insured?.user?.profile?.lastName}`,
          insuredAvatar: p?.insured?.user?.profile?.profilePicture,
          policyNumber: p.policyNumber,
          policyIcon: p.insuranceCompany.logoPath,
          accountValue: formatCurrency(review?.policyAccountValue),
          surrenderValue: formatCurrency(review?.policySurrenderValue),
          benefit: formatCurrency(review?.policyDB),
          ownerName: summaryData?.entity?.title,
          ownerLogo: summaryData?.entity?.logoUrl,
          beneficiary: p.beneficiary,
          primaryColor: branding.primaryColor,
          primaryColorDark: branding.primaryColorDark,
          insuranceCompanyLogo: branding?.primaryLogo,
          insuranceCompanyLogoDark: branding?.primaryLogoDark,
          insuranceCompanyName: p?.insuranceCompany?.name,
          policyType: p.policyType,
          policySubType: p.policySubType,
        };
      })
    : undefined;

  const assetRows = summaryData
    ? (summaryData.outsideAssetsLedger ?? []).map((a) => ({
        id: a.id,
        type: titleCase(a.type),
        institution: a.institution,
        accountId: a.accountNumber,
        value: formatCurrency(a.value),
        creditPercent: `${a.creditPercentage}%`,
        creditValue: formatCurrency(a.creditValue),
      }))
    : undefined;

  // BS/KP: Policy Overview is the live sum across selected people. Other plan
  // types keep using the server-provided aggregate.
  const policyOverviewData = isPeoplePlan
    ? {
        shareholders: overview.peopleCount,
        lifePolicies: overview.lifeCount,
        disabilityPolicies: overview.disabilityCount,
        shareholderValue: formatCurrency(overview.value),
        lifeBenefits: formatCurrency(overview.lifeBenefits),
        disabilityBenefits: formatCurrency(overview.disabilityBenefits),
      }
    : summaryData?.policyOverview
    ? {
        shareholders: summaryData.policyOverview.shareholders,
        lifePolicies: summaryData.policyOverview.lifePolicies,
        disabilityPolicies: summaryData.policyOverview.disabilityPolicies,
        shareholderValue: formatCurrency(summaryData.policyOverview.shareholderValue),
        lifeBenefits: formatCurrency(summaryData.policyOverview.lifeBenefits),
        disabilityBenefits: formatCurrency(summaryData.policyOverview.disabilityBenefits),
      }
    : undefined;

  return (
    <div className="flex flex-col gap-2 pb-6 mt-[124px] relative">
      {loading && (
        <div className="flex justify-center py-8">
          <ProgressSpinner style={{ width: 40, height: 40 }} />
        </div>
      )}

      {isReviewSummary && (
        <YearStepper
          isDark={isDarkTheme}
          steps={yearSteps}
          phases={hasCreditPhases ? ["preCredit", "postCredit"] : undefined}
          selectedPhase={selectedCreditType}
          selectedYearNumber={selectedYear}
          allowLockedClick
          onYearClick={(step) => {
            setSelectedYear(step.yearNumber);
            setSessionStore("ircSelectedYear", step.yearNumber);
            window.dispatchEvent(
              new CustomEvent("ircSelectionChanged", { detail: { year: step.yearNumber } }),
            );
          }}
          onPhaseClick={(phase) => {
            setSelectedCreditType(phase);
            setSessionStore("ircSelectedCreditType", phase);
            window.dispatchEvent(
              new CustomEvent("ircSelectionChanged", { detail: { creditType: phase } }),
            );
          }}
        />
      )}

      <PlanOverviewSection
        planPhasesProps={planPhasesProps ?? undefined}
        contacts={people}
        selectedIds={selectedIds}
        onToggle={togglePerson}
      />

      {sessionPlanType !== ProposalTypes.BuySell &&
        sessionPlanType !== ProposalTypes.KeyPersonInsurance && (
          <FinancingCard rows={financingRows} />
        )}

      {(sessionPlanType === ProposalTypes.BuySell ||
        sessionPlanType === ProposalTypes.KeyPersonInsurance) && (
        <PolicyOverviewCard data={policyOverviewData} />
      )}

      <PolicyInformationCard policies={policyRows} />

      {sessionPlanType === ProposalTypes.PremiumFinance && (
        <OutsidePledgedAssetsCard assets={assetRows} />
      )}

      <div className="bg-[#F5F9FF] border border-[#ABC9FB] rounded-2xl p-2 flex gap-2 items-center mt-4">
        <WarningHex width="16" fill="#326FD1" />
        <span className="text-[#326FD1] text-[12px]">
          Data shown is based on the latest available inputs. Always confirm directly with the
          insurance carrier. See Disclaimer Page for details.
        </span>
      </div>
    </div>
  );
};

export default ReviewCenterOverview;
